import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Plus, MessageCircle, Briefcase, ClipboardList, MapPin } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const resolveName = (nameObj, lang) =>
  nameObj?.[lang] ?? nameObj?.['es'] ?? nameObj?.['en'] ?? '';

// ── Shared status badge ───────────────────────────────────────────────────────
const StatusBadge = ({ status, t }) => {
  const map = {
    pending:                'bg-yellow-100 text-yellow-800',
    negotiating:            'bg-amber-100 text-amber-800',
    provider_accepted:      'bg-blue-100 text-blue-800',
    payment_pending:        'bg-blue-100 text-blue-800',
    in_progress:            'bg-green-100 text-green-800',
    awaiting_confirmation:  'bg-amber-100 text-amber-800',
    completed:              'bg-gray-100 text-gray-700',
    cancelled:              'bg-gray-100 text-gray-500',
    disputed:               'bg-red-100 text-red-800',
    accepted:               'bg-blue-100 text-blue-800',
  };
  const labelKey = {
    pending:               'status_pending',
    negotiating:           'dashboard.pending_acceptance',
    provider_accepted:     'dashboard.pending_acceptance',
    payment_pending:       'dashboard.awaiting_payment',
    in_progress:           'dashboard.in_progress',
    awaiting_confirmation: 'dashboard.awaiting_confirmation',
    completed:             'dashboard.completed',
    cancelled:             'dashboard.cancelled',
    disputed:              'dashboard.disputed',
    accepted:              'status_accepted',
  };
  const key = labelKey[status] || `service.status_${status}`;
  const label = key.includes('.') ? t(key) : t(`service.${key}`);
  return (
    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${map[status] || 'bg-gray-100 text-gray-700'}`}>
      {label}
    </span>
  );
};

// ── Client Dashboard ──────────────────────────────────────────────────────────
const ClientDashboard = ({ requests, categories, subcategories, t, i18n, navigate }) => {
  const catMap = useMemo(() => Object.fromEntries(categories.map(c => [c.id, c])), [categories]);
  const subMap = useMemo(() => {
    const m = {};
    subcategories.forEach(s => { if (s.id) m[s.id] = s; if (s.slug) m[s.slug] = s; });
    return m;
  }, [subcategories]);

  const resolveLabel = (r) => {
    const cat = r.category_name ? resolveName(r.category_name, i18n.language) : resolveName(catMap[r.category_id]?.name, i18n.language);
    const sub = r.subcategory_name ? resolveName(r.subcategory_name, i18n.language) : resolveName(subMap[r.subcategory_id]?.name, i18n.language);
    if (cat && sub) return `${cat} › ${sub}`;
    return cat || sub || t('service.request_title');
  };

  return (
    <>
      <div className="mb-6 flex gap-4">
        <Button size="lg" onClick={() => navigate('/service-request')} className="rounded-full"
          style={{ background: 'hsl(48 100% 50%)', color: 'hsl(215 25% 12%)' }} data-testid="new-request-button">
          <Plus className="w-5 h-5 mr-2" />{t('dashboard.new_request')}
        </Button>
        <Button size="lg" variant="outline" onClick={() => navigate('/catalog')} className="rounded-full" data-testid="browse-services-button">
          Browse Service Providers
        </Button>
      </div>

      <Tabs defaultValue="all" className="w-full">
        <TabsList className="mb-6">
          <TabsTrigger value="all" data-testid="tab-all">All Requests</TabsTrigger>
          <TabsTrigger value="active" data-testid="tab-active">Active</TabsTrigger>
          <TabsTrigger value="completed" data-testid="tab-completed">Completed</TabsTrigger>
        </TabsList>

        {['all', 'active', 'completed'].map(tab => {
          const filtered = tab === 'all' ? requests
            : tab === 'active' ? requests.filter(r => ['pending', 'negotiating', 'provider_accepted', 'payment_pending', 'in_progress', 'awaiting_confirmation'].includes(r.status))
            : requests.filter(r => r.status === 'completed');
          return (
            <TabsContent key={tab} value={tab} className="space-y-4">
              {filtered.length === 0 ? (
                <Card><CardContent className="py-12 text-center text-muted-foreground">No service requests yet</CardContent></Card>
              ) : filtered.map(request => (
                <Card key={request.id} className="hover:shadow-md transition-shadow" data-testid={`request-card-${request.id}`}>
                  <CardContent className="pt-6">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="text-xs text-muted-foreground mb-1 font-medium">{resolveLabel(request)}</div>
                        <h3 className="text-lg font-semibold mb-2">
                          {request.title?.[i18n.language] || request.title?.es || request.title?.en || ''}
                        </h3>
                        <p className="text-sm text-muted-foreground mb-4">
                          {request.description?.[i18n.language] || request.description?.es || ''}
                        </p>
                        <div className="flex gap-4 text-sm">
                          <span className="flex items-center gap-1">
                            <MessageCircle className="w-4 h-4" />
                            {request.proposals?.length || 0} proposals
                          </span>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <StatusBadge status={request.status} t={t} />
                        <Button variant="outline" size="sm" onClick={() => navigate(`/service/${request.id}`)} data-testid={`view-request-${request.id}`}>
                          View Details
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </TabsContent>
          );
        })}
      </Tabs>
    </>
  );
};

// ── Provider Dashboard ────────────────────────────────────────────────────────
const ProviderDashboard = ({ myJobs, openRequests, t, i18n, navigate, onRefresh, user }) => {
  const [actioning, setActioning] = useState(null);

  const resolveLabel = (r) => {
    const cat = resolveName(r.category_name, i18n.language);
    const sub = resolveName(r.subcategory_name, i18n.language);
    if (cat && sub) return `${cat} › ${sub}`;
    return cat || sub || '';
  };

  const timeAgo = (iso) => {
    if (!iso) return '';
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  const doAction = async (endpoint, requestId) => {
    setActioning(requestId);
    try {
      await axios.post(`${API}/service-requests/${requestId}/${endpoint}`);
      toast.success('Updated');
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setActioning(null);
    }
  };

  return (
    <>
      {/* Section 1 — My Jobs */}
      <div className="mb-8">
        <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
          <Briefcase className="w-5 h-5" />
          {t('dashboard.my_jobs')}
        </h2>
        {myJobs.length === 0 ? (
          <Card><CardContent className="py-8 text-center text-muted-foreground">No active jobs yet</CardContent></Card>
        ) : (
          <div className="space-y-3">
            {myJobs.map(job => (
              <Card
                key={job.id}
                className={`hover:shadow-md transition-shadow cursor-pointer ${job.status === 'disputed' ? 'border-red-300' : ''}`}
                onClick={() => navigate(`/service/${job.id}`)}
              >
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-muted-foreground mb-0.5">{resolveLabel(job)}</p>
                      <p className="font-semibold text-sm truncate">
                        {job.title?.[i18n.language] || job.title?.es || resolveLabel(job)}
                      </p>
                      {job.client_name && (
                        <p className="text-xs text-muted-foreground mt-0.5">Client: {job.client_name}</p>
                      )}
                      {(job.location?.address || job.address) && (
                        <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                          <MapPin className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate" style={{ maxWidth: 200 }}>
                            {(job.location?.address || job.address || '').slice(0, 60)}
                          </span>
                        </p>
                      )}
                      {(job.preferred_date || job.agreed_date) && (
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {new Date(job.agreed_date || job.preferred_date).toLocaleDateString(i18n.language)}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-2 flex-shrink-0">
                      <StatusBadge status={job.status} t={t} />
                      {/* Inline card actions */}
                      {(job.status === 'negotiating') && (
                        <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                          <Button size="sm" disabled={actioning === job.id}
                            onClick={() => doAction('provider-accept', job.id)}
                            style={{ background: 'hsl(120 100% 30%)', color: 'white' }}>
                            {t('dashboard.accept')}
                          </Button>
                          <Button size="sm" variant="outline" disabled={actioning === job.id}
                            onClick={() => doAction('cancel', job.id)}>
                            {t('dashboard.decline')}
                          </Button>
                        </div>
                      )}
                      {job.status === 'in_progress' && (
                        <div onClick={e => e.stopPropagation()}>
                          <Button size="sm" disabled={actioning === job.id}
                            onClick={() => doAction('confirm-completion', job.id)}
                            style={{ background: 'hsl(215 100% 34%)', color: 'white' }}>
                            {t('dashboard.confirm_completion')}
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Section 2 — Open Requests */}
      <div>
        <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
          <ClipboardList className="w-5 h-5" />
          {t('dashboard.open_requests')}
        </h2>
        {(user?.verification_tier ?? 1) < 2 && (
          <div className="mb-4 flex items-center justify-between gap-4 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
            <p className="text-sm text-amber-800">{t('provider.verify_banner')}</p>
            <Button size="sm" variant="outline" onClick={() => navigate('/provider/verification')}>
              {t('provider.verify_now')}
            </Button>
          </div>
        )}
        {openRequests.length === 0 ? (
          <Card><CardContent className="py-8 text-center text-muted-foreground">{t('dashboard.no_open_requests')}</CardContent></Card>
        ) : (
          <div className="space-y-3">
            {openRequests.map(req => (
              <Card key={req.id} className="hover:shadow-md transition-shadow">
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-muted-foreground mb-0.5">{resolveLabel(req)}</p>
                      <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                        {(req.description?.[i18n.language] || req.description?.es || '').slice(0, 100)}
                      </p>
                      {(req.location?.address || req.address) && (
                        <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                          <MapPin className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate" style={{ maxWidth: 220 }}>
                            {(req.location?.address || req.address || '').slice(0, 60)}
                          </span>
                        </p>
                      )}
                      <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                        <span>{timeAgo(req.created_at)}</span>
                        <span>{req.proposals?.length || 0} proposals</span>
                      </div>
                    </div>
                    <div className="flex-shrink-0">
                      <Button size="sm" variant="outline" onClick={() => navigate(`/service/${req.id}`)}>
                        {t('dashboard.view_and_propose')}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

// ── Admin Dashboard ───────────────────────────────────────────────────────────
const AdminDashboard = ({ stats }) => (
  stats ? (
    <div className="grid md:grid-cols-4 gap-6">
      {[
        { label: 'Total Users', value: stats.total_users, testid: 'admin-stats-users' },
        { label: 'Providers', value: stats.total_providers, testid: 'admin-stats-providers' },
        { label: 'Total Requests', value: stats.total_requests, testid: 'admin-stats-requests' },
        { label: 'Active Requests', value: stats.active_requests, testid: 'admin-stats-active' },
      ].map(({ label, value, testid }) => (
        <Card key={label} data-testid={testid}>
          <CardHeader className="pb-3"><CardTitle className="text-sm font-medium text-muted-foreground">{label}</CardTitle></CardHeader>
          <CardContent><div className="text-3xl font-bold">{value}</div></CardContent>
        </Card>
      ))}
    </div>
  ) : null
);

// ── Root ──────────────────────────────────────────────────────────────────────
const Dashboard = () => {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [requests, setRequests] = useState([]);       // client / admin flat list
  const [myJobs, setMyJobs] = useState([]);            // provider my_jobs
  const [openRequests, setOpenRequests] = useState([]); // provider open_requests
  const [stats, setStats] = useState(null);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    if (!user) return;
    try {
      const [reqRes, catsRes, subsRes] = await Promise.all([
        axios.get(`${API}/service-requests`),
        axios.get(`${API}/categories`),
        axios.get(`${API}/subcategories`),
      ]);

      setCategories(catsRes.data);
      setSubcategories(subsRes.data);

      if (user.role === 'provider') {
        setMyJobs(reqRes.data.my_jobs || []);
        setOpenRequests(reqRes.data.open_requests || []);
      } else {
        setRequests(Array.isArray(reqRes.data) ? reqRes.data : []);
      }

      if (user.role === 'admin') {
        const statsRes = await axios.get(`${API}/admin/stats`);
        setStats(statsRes.data);
      }
    } catch {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (!user) { navigate('/auth'); return; }
    fetchData();
  }, [user, navigate, fetchData]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="text-xl">Loading...</div></div>;

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h2 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Outfit' }}>
          {t(user?.role === 'client' ? 'dashboard.client_title' : user?.role === 'admin' ? 'dashboard.admin_title' : 'dashboard.provider_title')}
        </h2>
      </div>

      {user?.role === 'admin' && <AdminDashboard stats={stats} />}
      {user?.role === 'client' && (
        <ClientDashboard requests={requests} categories={categories} subcategories={subcategories} t={t} i18n={i18n} navigate={navigate} />
      )}
      {user?.role === 'provider' && (
        <ProviderDashboard myJobs={myJobs} openRequests={openRequests} t={t} i18n={i18n} navigate={navigate} onRefresh={fetchData} user={user} />
      )}
    </div>
  );
};

export default Dashboard;
